
import { Vue, Component, Watch } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
import { SET_USER } from "../store/action-types";
import { AUTH_METHOD } from "../types";
import BaseView from "./BaseView";
import formValidationRules from "../utils/formValidationRules";
import LoadingStateController from "../utils/LoadingStateController";
import User from "../entities/User";
import UsersRepository from "../repositories/implementations/UsersRepository";

@Component
export default class SignInSignUpView extends BaseView {
  @Getter("auth/user") user: null | User;

  @Action(`auth/${SET_USER}`) setUser: (user: null | User) => void;

  currentAuthMethod: null | AUTH_METHOD = null;

  email = "";

  password = "";

  showPassword = false;

  errors: Record<string, string> = {
    general: "",
    email: "",
    password: "",
  };

  get disableUI() {
    return LoadingStateController.state.isLoading;
  }

  get isSignUpMode() {
    return this.$route.name === "sign-up";
  }

  get formValidationRules() {
    return formValidationRules;
  }

  @Watch("$route")
  public watchRoute() {
    this.email = "";
    this.password = "";
    this.showPassword = false;
    this.resetErrors();
  }

  @Watch("email")
  public watchEmail() {
    this.errors.email = "";
  }

  @Watch("password")
  public watchPassword() {
    this.errors.password = "";
  }

  resetErrors() {
    this.errors.email = "";
    this.errors.password = "";
    this.errors.general = "";

    // reset validation so that the messages are translated the next time the fields are validated
    (this.$refs.form as Vue & { resetValidation: () => void }).resetValidation();
  }

  isFormValid(): boolean {
    return (this.$refs.form as Vue & { validate: () => boolean }).validate();
  }

  onLanguageSwitch() {
    this.resetErrors();
  }

  async authenticate(authType: AUTH_METHOD) {
    this.resetErrors();
    this.currentAuthMethod = authType;
    LoadingStateController.start("authenticate");

    try {
      const usersRepository = new UsersRepository();

      switch (authType) {
        case AUTH_METHOD.SIGN_UP_WITH_EMAIL:
          await usersRepository.signUpWithEmailAndPassword(this.email, this.password);
          break;
        case AUTH_METHOD.SIGN_IN_WITH_EMAIL:
          await usersRepository.signInWithEmailAndPassword(this.email, this.password);
          break;
        default:
          throw new Error(`Authentication error: unknown auth type ${authType}`);
      }

      // user is saved to the store by handler "onAuthStateChanged" in main.ts

      if (this.$route.name !== "projects") {
        this.$router.replace({ name: "projects" });
      }
    } catch (error: any) {
      if (error.isPasswordRelated) {
        this.errors.password = error.message;
      } else if (error.isEmailRelated) {
        this.errors.email = error.message;
      } else {
        this.errors.general = error.message;
      }
    } finally {
      this.currentAuthMethod = null;
      LoadingStateController.stop("authenticate");
    }
  }

  normalizeEmail() {
    if (!this.email) {
      return;
    }

    this.email = this.email.trim().toLowerCase().replace(/[\s,]/g, "");
  }

  async onFormSubmit() {
    this.normalizeEmail();

    if (!this.isFormValid()) {
      return false;
    }

    if (this.isSignUpMode) {
      await this.authenticate(AUTH_METHOD.SIGN_UP_WITH_EMAIL);
    } else {
      await this.authenticate(AUTH_METHOD.SIGN_IN_WITH_EMAIL);
    }

    // return true to prevent the default form submit action
    return true;
  }
}
